import { useEffect, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import type { DateRange } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { monthLabel, recentMonths, previousMonth, groupCountry, COUNTRY_GROUPS, countryLabel } from "./helpers";
import { fmtN } from "./helpers";
import type { FilterPillSpec } from "../../components/FilterPills";

// Minimum row shape required by applyFilters / applyNonTimeFilters. Pages
// pass their own row type (e.g. UserRow), which must extend this.
export type FilterableUser = {
  user_role: string | null;
  country_code: string | null;
  marloo_active_user_count: number;
  had_demo: boolean;
  first_demo_date: string | null;
  created_at_utc: string;
  // Optional company-propagated demo fields. Present on Sign-up Activation
  // rows; absent on pages that don't propagate (e.g. Demo Funnel).
  company_had_demo?: boolean;
  company_first_demo_date?: string | null;
};

function applyDemoPropagation<T extends FilterableUser>(u: T, mode: "user" | "company"): T {
  if (mode !== "company") return u;
  if (u.company_had_demo == null) return u;
  return {
    ...u,
    had_demo: u.company_had_demo,
    first_demo_date: u.company_first_demo_date ?? null,
  };
}

export const COMPANY_SIZE_BUCKETS: { key: string; label: string }[] = [
  { key: "1", label: "1 (solo)" },
  { key: "2-10", label: "2–10" },
  { key: "11-50", label: "11–50" },
  { key: "50+", label: "50+" },
];

export function prettifyDemoType(t: string): string {
  return t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function inCompanySize(n: number, bucket: string): boolean {
  switch (bucket) {
    case "1": return n === 1;
    case "2-10": return n >= 2 && n <= 10;
    case "11-50": return n >= 11 && n <= 50;
    case "50+": return n > 50;
    default: return false;
  }
}

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseRange(from: string, to: string): DateRange | undefined {
  if (!from && !to) return undefined;
  const f = from ? new Date(`${from}T00:00:00`) : undefined;
  const t = to ? new Date(`${to}T00:00:00`) : undefined;
  return { from: f, to: t };
}

export type Filters = {
  months: string[];
  countries: string[];
  roles: string[];
  companySizes: string[];
  excludeSupport: boolean;
  granularity?: "month" | "day";
  cohortDayFrom?: string | null;
  cohortDayTo?: string | null;
  demoPropagation?: "user" | "company";
  // "first_demo_date": demo users bucketed by their first demo (default).
  // "signup_date":     demo users bucketed by their Marloo sign-up date;
  //                    attendees who never signed up are dropped from the
  //                    denominator. Anyone with had_demo at any point counts
  //                    as demo regardless of the order vs sign-up.
  demoBucketing?: "first_demo_date" | "signup_date";
  // Demo Conversion only. Empty = all types. Rows with null demo_type
  // (pre-Feb-2026 backfill, no follow_up_context) always pass through so the
  // historical cohort isn't silently dropped by this filter.
  demoTypes?: string[];
  // Demo Conversion secret URL only. Empty = all demoers. Filters rows by
  // organiser_email of the demo. Page-level filter; ripples through every
  // panel including the cohort-level demos count.
  demoers?: string[];
  // Sign-up Activation v2 only. Days-since-signup window driving the
  // histogram cohort filter and x-axis. Surfaced in FiltersBar when the
  // page passes observationWindowOptions.
  observationDays?: number;
};

export function applyNonTimeFilters<T extends FilterableUser>(users: T[], f: Filters): T[] {
  const countrySet = new Set(f.countries);
  const roleSet = new Set(f.roles);
  const sizeSet = new Set(f.companySizes);
  const propMode = f.demoPropagation ?? "user";
  const out: T[] = [];
  for (const raw of users) {
    if (f.excludeSupport && raw.user_role === "support") continue;
    if (countrySet.size > 0 && !countrySet.has(groupCountry(raw.country_code))) continue;
    if (roleSet.size > 0 && !roleSet.has(raw.user_role ?? "unknown")) continue;
    if (sizeSet.size > 0) {
      const match = Array.from(sizeSet).some((b) => inCompanySize(raw.marloo_active_user_count, b));
      if (!match) continue;
    }
    out.push(applyDemoPropagation(raw, propMode));
  }
  return out;
}

export function applyFilters<T extends FilterableUser>(users: T[], f: Filters): T[] {
  const monthSet = new Set(f.months);
  const countrySet = new Set(f.countries);
  const roleSet = new Set(f.roles);
  const sizeSet = new Set(f.companySizes);
  const dayMode = f.granularity === "day" && (!!f.cohortDayFrom || !!f.cohortDayTo);
  const dayFrom = f.cohortDayFrom ?? null;
  const dayTo = f.cohortDayTo ?? f.cohortDayFrom ?? null;
  const bySignupDate = f.demoBucketing === "signup_date";
  const propMode = f.demoPropagation ?? "user";
  const out: T[] = [];
  for (const raw of users) {
    if (f.excludeSupport && raw.user_role === "support") continue;
    const u = applyDemoPropagation(raw, propMode);
    // Demo-path users are bucketed by their first demo date by default; the
    // signup_date toggle re-buckets them by their Marloo sign-up date so the
    // cohort axis reads as "signed up in month X" regardless of demo timing.
    const cohortDate = !bySignupDate && u.had_demo && u.first_demo_date
      ? u.first_demo_date
      : (u.created_at_utc ?? "");
    if (dayMode) {
      const d = cohortDate.slice(0, 10);
      if (dayFrom && d < dayFrom) continue;
      if (dayTo && d > dayTo) continue;
    } else if (monthSet.size > 0) {
      if (!monthSet.has(cohortDate.slice(0, 7))) continue;
    }
    if (countrySet.size > 0 && !countrySet.has(groupCountry(u.country_code))) continue;
    if (roleSet.size > 0 && !roleSet.has(u.user_role ?? "unknown")) continue;
    if (sizeSet.size > 0) {
      const match = Array.from(sizeSet).some((b) => inCompanySize(u.marloo_active_user_count, b));
      if (!match) continue;
    }
    out.push(u);
  }
  return out;
}

// Convert the page-level Filters state into a flat list of read-only pill
// specs for chart-tile headers. Only narrowing values surface (e.g. an
// empty country list produces no pill, since "All countries" is the
// default). Mode toggles (demo bucketing, propagation) appear because they
// affect interpretation even when they don't filter rows. excludeSupport
// only surfaces when flipped OFF (the default is true).
export function filtersToPills(f: Filters): FilterPillSpec[] {
  const out: FilterPillSpec[] = [];

  // Cohort
  if (f.granularity === "day" && (f.cohortDayFrom || f.cohortDayTo)) {
    const from = f.cohortDayFrom ?? "";
    const to = f.cohortDayTo ?? from;
    out.push({
      kind: "cohort",
      label: from && to && from !== to ? `${from} → ${to}` : (from || to),
    });
  } else if (f.months.length === 1) {
    out.push({ kind: "cohort", label: monthLabel(f.months[0] ?? "") });
  } else if (f.months.length > 1) {
    out.push({
      kind: "cohort",
      label: `${f.months.length} months`,
      detail: f.months.map((m) => monthLabel(m)),
    });
  } else {
    // Empty months + month granularity = all months in scope.
    out.push({ kind: "cohort", label: "All time" });
  }

  if (f.countries.length > 0) {
    const first = f.countries[0] ?? "";
    const isKnown = (COUNTRY_GROUPS as readonly string[]).includes(first);
    const labelFor = (c: string) =>
      (COUNTRY_GROUPS as readonly string[]).includes(c)
        ? countryLabel(c as typeof COUNTRY_GROUPS[number])
        : c;
    out.push({
      kind: "country",
      label: f.countries.length === 1
        ? (isKnown ? countryLabel(first as typeof COUNTRY_GROUPS[number]) : first)
        : `${f.countries.length} countries`,
      detail: f.countries.length > 1 ? f.countries.map(labelFor) : undefined,
    });
  }
  if (f.roles.length > 0) {
    out.push({
      kind: "role",
      label: f.roles.length === 1 ? (f.roles[0] ?? "") : `${f.roles.length} roles`,
      detail: f.roles.length > 1 ? f.roles : undefined,
    });
  }
  if (f.companySizes.length > 0) {
    const labelFor = (k: string) =>
      COMPANY_SIZE_BUCKETS.find((b) => b.key === k)?.label ?? k;
    out.push({
      kind: "size",
      label: f.companySizes.length === 1 ? `${labelFor(f.companySizes[0] ?? "")} users` : `${f.companySizes.length} sizes`,
      detail: f.companySizes.length > 1 ? f.companySizes.map((k) => `${labelFor(k)} users`) : undefined,
    });
  }
  if (f.demoTypes && f.demoTypes.length > 0) {
    out.push({
      kind: "demoType",
      label: f.demoTypes.length === 1 ? prettifyDemoType(f.demoTypes[0] ?? "") : `${f.demoTypes.length} demo types`,
      detail: f.demoTypes.length > 1 ? f.demoTypes.map(prettifyDemoType) : undefined,
    });
  }
  if (f.demoers && f.demoers.length > 0) {
    out.push({
      kind: "demoer",
      label: f.demoers.length === 1 ? (f.demoers[0] ?? "") : `${f.demoers.length} demoers`,
      detail: f.demoers.length > 1 ? f.demoers : undefined,
    });
  }

  // Mode toggles — affect interpretation even when not narrowing rows.
  if (f.demoBucketing === "signup_date") {
    out.push({ kind: "mode", label: "Bucketed by sign-up date" });
  }
  if (f.demoPropagation === "company") {
    out.push({ kind: "mode", label: "Company-propagated demos" });
  }

  // excludeSupport: default true. Only surface the divergent state.
  if (!f.excludeSupport) {
    out.push({ kind: "support", label: "Including support roles" });
  }

  return out;
}

type FiltersBarProps = {
  filters: Filters;
  onChange: (next: Filters) => void;
  availableCountries: string[];
  availableRoles: string[];
  availableDemoTypes?: string[];
  availableDemoers?: string[];
  usersInView: number;
  cohortVariant?: "multi-month" | "single-with-day";
  showDemoPropagation?: boolean;
  showDemoBucketing?: boolean;
  showCohort?: boolean;
  observationWindowOptions?: number[];
};

export function FiltersBar({ filters, onChange, availableCountries, availableRoles, availableDemoTypes, availableDemoers, usersInView, cohortVariant = "multi-month", showDemoPropagation = false, showDemoBucketing = true, showCohort = true, observationWindowOptions }: FiltersBarProps) {
  const months = recentMonths();
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => onChange({ ...filters, [key]: value });

  const presentGroups = new Set(availableCountries);
  const countryOpts = COUNTRY_GROUPS
    .filter((g) => presentGroups.has(g))
    .map((g) => ({ value: g, label: countryLabel(g) }));
  const roleOpts = availableRoles.map((r) => ({ value: r, label: r }));
  const sizeOpts = COMPANY_SIZE_BUCKETS.map((b) => ({ value: b.key, label: b.label }));
  const demoTypeOpts = (availableDemoTypes ?? []).map((t) => ({ value: t, label: prettifyDemoType(t) }));
  const demoerOpts = (availableDemoers ?? []).map((d) => ({ value: d, label: d }));

  return (
    <>
    <div className="filterbar-meta">
      <span className="cohort-meta">
        <span className="status-dot" />
        {fmtN(usersInView)} users in view
      </span>
    </div>
    <section className="filterbar">
      <span className="filterbar-label"><i className="ti ti-filter" />Filters</span>
      <div className="filterbar-stack">
      <div className="filterbar-row">
        {showCohort && (
          cohortVariant === "single-with-day" ? (
            <CohortPicker filters={filters} onChange={onChange} />
          ) : (
            <MultiChip
              label="Cohort"
              allLabel="All time"
              pluralNoun="months"
              options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
              selected={filters.months}
              onChange={(next) => set("months", next)}
            />
          )
        )}
        <MultiChip
          label="Country"
          allLabel="All countries"
          pluralNoun="countries"
          options={countryOpts}
          selected={filters.countries}
          onChange={(next) => set("countries", next)}
        />
        <MultiChip
          label="Role"
          allLabel="All roles"
          pluralNoun="roles"
          options={roleOpts}
          selected={filters.roles}
          onChange={(next) => set("roles", next)}
        />
        <MultiChip
          label="Company size"
          allLabel="All sizes"
          pluralNoun="sizes"
          options={sizeOpts}
          selected={filters.companySizes}
          onChange={(next) => set("companySizes", next)}
        />
        {demoTypeOpts.length > 0 && (
          <MultiChip
            label="Demo type"
            allLabel="All types"
            pluralNoun="types"
            options={demoTypeOpts}
            selected={filters.demoTypes ?? []}
            onChange={(next) => set("demoTypes", next)}
          />
        )}
        {demoerOpts.length > 0 && (
          <MultiChip
            label="Demoer"
            allLabel="All demoers"
            pluralNoun="demoers"
            options={demoerOpts}
            selected={filters.demoers ?? []}
            onChange={(next) => set("demoers", next)}
          />
        )}
        <button
          type="button"
          className="reset-icon"
          title="Reset"
          aria-label="Reset"
          onClick={() => onChange({
            ...filters,
            months: [previousMonth()],
            countries: [],
            roles: [],
            companySizes: [],
            granularity: "month",
            cohortDayFrom: null,
            cohortDayTo: null,
            demoTypes: [],
            demoers: [],
            excludeSupport: true,
          })}
        >
          <i className="ti ti-refresh" />
        </button>
      </div>
      <div className="filterbar-row">
        {showDemoPropagation && (
          <DemoPropagationToggle
            value={filters.demoPropagation ?? "user"}
            onChange={(v) => set("demoPropagation", v)}
          />
        )}
        {showDemoBucketing && (
          <DemoBucketingToggle
            value={filters.demoBucketing ?? "first_demo_date"}
            onChange={(v) => set("demoBucketing", v)}
          />
        )}
        {observationWindowOptions && observationWindowOptions.length > 0 && (
          <ObservationWindowToggle
            options={observationWindowOptions}
            value={filters.observationDays ?? observationWindowOptions[0]!}
            onChange={(v) => set("observationDays", v)}
          />
        )}
        <label className="filter-switch" title="Exclude support roles">
          <input
            type="checkbox"
            checked={filters.excludeSupport}
            onChange={(e) => set("excludeSupport", e.target.checked)}
          />
          <span className="track"><span className="thumb" /></span>
          <span className="label-text">Exclude support roles</span>
        </label>
      </div>
      </div>
    </section>
    </>
  );
}

export function MultiChip({
  label,
  allLabel,
  pluralNoun,
  options,
  selected,
  onChange,
}: {
  label: string;
  allLabel: string;
  pluralNoun: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const display = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? selected[0] ?? "")
      : `${selected.length} ${pluralNoun}`;

  const toggle = (v: string, multi: boolean) => {
    if (multi) {
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
      onChange(next);
    } else {
      onChange(selected.length === 1 && selected[0] === v ? [] : [v]);
    }
  };

  return (
    <span className="filter-chip month-multi" ref={ref}>
      <span className="chip-label">{label}:</span>
      <span className="chip-value">{display}</span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${display}`}
      />
      {open && (
        <div className="month-multi-popover" role="listbox" onClick={(e) => e.stopPropagation()}>
          <label className="row">
            <input
              type="checkbox"
              checked={selected.length === 0}
              onChange={() => onChange([])}
            />
            <span>{allLabel}</span>
          </label>
          <div className="divider" />
          <span className="multi-hint">Shift+click to select multiple</span>
          {options.map((o) => (
            <label key={o.value} className="row" onClick={(e) => { e.preventDefault(); toggle(o.value, e.shiftKey); }}>
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => {}}
                tabIndex={-1}
                style={{ pointerEvents: "none" }}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

function CohortPicker({ filters, onChange }: { filters: Filters; onChange: (next: Filters) => void }) {
  const granularity = filters.granularity ?? "month";
  const isDay = granularity === "day";
  const dayFrom = filters.cohortDayFrom ?? "";
  const dayTo = filters.cohortDayTo ?? "";
  const months = recentMonths();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    return () => window.removeEventListener("mousedown", onDoc);
  }, [open]);

  const display = isDay
    ? (dayFrom && dayTo
        ? (dayFrom === dayTo ? dayFrom : `${dayFrom} → ${dayTo}`)
        : (dayFrom || dayTo || "Pick a date range"))
    : filters.months.length === 0
      ? "All time"
      : filters.months.length === 1
        ? monthLabel(filters.months[0] ?? "")
        : `${filters.months.length} months`;

  const toggleMonth = (m: string, multi: boolean) => {
    const sel = filters.months;
    let next: string[];
    if (multi) {
      next = sel.includes(m) ? sel.filter((x) => x !== m) : [...sel, m];
    } else {
      next = sel.length === 1 && sel[0] === m ? [] : [m];
    }
    onChange({ ...filters, months: next, granularity: "month", cohortDayFrom: null, cohortDayTo: null });
  };
  const setAllTime = () => {
    onChange({ ...filters, months: [], granularity: "month", cohortDayFrom: null, cohortDayTo: null });
  };
  const onRangeChange = (r: DateRange | undefined) => {
    const from = r?.from ? toIso(r.from) : null;
    const to = r?.to ? toIso(r.to) : from;
    onChange({ ...filters, cohortDayFrom: from, cohortDayTo: to, granularity: "day", months: [] });
  };
  const onToggleDay = (nextOn: boolean) => {
    if (nextOn) {
      const seedMonth = filters.months[0] ?? "";
      const seedDay = seedMonth ? `${seedMonth}-01` : "";
      onChange({ ...filters, granularity: "day", cohortDayFrom: seedDay || null, cohortDayTo: seedDay || null, months: [] });
    } else {
      const seedMonth = (dayFrom || dayTo) ? (dayFrom || dayTo).slice(0, 7) : "";
      onChange({ ...filters, granularity: "month", cohortDayFrom: null, cohortDayTo: null, months: seedMonth ? [seedMonth] : [] });
    }
  };

  return (
    <span className="filter-chip month-multi" ref={ref}>
      <span className="chip-label">Cohort:</span>
      <span className="chip-value">{display}</span>
      <i className="ti ti-chevron-down chevron" />
      <button
        type="button"
        className="chip-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Cohort: ${display}`}
      />
      {open && (
        <div className="month-multi-popover" role="listbox" onClick={(e) => e.stopPropagation()}>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={isDay}
              onChange={(e) => onToggleDay(e.target.checked)}
            />
            <span>Day-by-day (date range)</span>
          </label>
          <div className="divider" />
          {isDay ? (
            <div style={{ padding: "4px 0" }}>
              <DayPicker
                mode="range"
                numberOfMonths={1}
                selected={parseRange(dayFrom, dayTo)}
                onSelect={(r) => onRangeChange(r)}
                defaultMonth={parseRange(dayFrom, dayTo)?.from ?? new Date()}
              />
              <div style={{ fontSize: 11, color: "var(--fg-3)", padding: "0 6px" }}>
                {dayFrom && dayTo ? (dayFrom === dayTo ? dayFrom : `${dayFrom} → ${dayTo}`) : "Click a start date, then an end date."}
              </div>
            </div>
          ) : (
            <>
              <label className="row">
                <input
                  type="checkbox"
                  checked={filters.months.length === 0}
                  onChange={setAllTime}
                />
                <span>All time</span>
              </label>
              <div className="divider" />
              <span className="multi-hint">Shift+click to select multiple</span>
              {months.map((m) => (
                <label
                  key={m}
                  className="row"
                  onClick={(e) => { e.preventDefault(); toggleMonth(m, e.shiftKey); }}
                >
                  <input
                    type="checkbox"
                    checked={filters.months.includes(m)}
                    onChange={() => {}}
                    tabIndex={-1}
                    style={{ pointerEvents: "none" }}
                  />
                  <span>{monthLabel(m)}</span>
                </label>
              ))}
            </>
          )}
        </div>
      )}
    </span>
  );
}

function ObservationWindowToggle({
  options,
  value,
  onChange,
}: {
  options: number[];
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <span className="filter-chip" title="Time window after sign-up shown on the histogram x-axis.">
      <span className="chip-label">Window:</span>
      <span className="chip-value">{value} days</span>
      <i className="ti ti-chevron-down chevron" />
      <select
        aria-label="Observation window"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          cursor: "pointer",
          border: "none",
          background: "transparent",
        }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt} days</option>
        ))}
      </select>
    </span>
  );
}

function DemoBucketingToggle({
  value,
  onChange,
}: {
  value: "first_demo_date" | "signup_date";
  onChange: (v: "first_demo_date" | "signup_date") => void;
}) {
  const opts: { key: "first_demo_date" | "signup_date"; label: string; title: string }[] = [
    { key: "first_demo_date", label: "By first demo", title: "Demo cohort = all demo attendees, bucketed by first demo date. Includes attendees who never signed up." },
    { key: "signup_date", label: "By sign-up date", title: "Demo cohort = signed-up users who had a demo at any point, bucketed by Marloo sign-up date." },
  ];
  return (
    <div role="group" aria-label="Demo bucketing mode" className="filter-segment">
      {opts.map((opt) => (
        <button
          key={opt.key}
          type="button"
          title={opt.title}
          onClick={() => onChange(opt.key)}
          className={value === opt.key ? "is-active" : undefined}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function DemoPropagationToggle({
  value,
  onChange,
}: {
  value: "user" | "company";
  onChange: (v: "user" | "company") => void;
}) {
  const opts: { key: "user" | "company"; label: string; title: string }[] = [
    { key: "user", label: "User-only demos", title: "Only users who personally attended a demo are demo-path." },
    { key: "company", label: "Company-propagated", title: "Anyone in a workspace where any user has had a demo is demo-path, bucketed by the company's first demo date." },
  ];
  return (
    <div role="group" aria-label="Demo attribution mode" className="filter-segment">
      {opts.map((opt) => (
        <button
          key={opt.key}
          type="button"
          title={opt.title}
          onClick={() => onChange(opt.key)}
          className={value === opt.key ? "is-active" : undefined}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
